/**
 * /projects/gantt — Gantt multi-progetto.
 * - Carica tutti i project del tenant
 * - Per ognuno carica le tasks (parallel queries)
 * - Usa `gantt-task-react`: row "macro" per ogni Project + sub-row per ProjectTask
 * - Color per healthStatus
 * - Toggle "Mostra archivio"
 * - Sidebar con filtri (status, priority, manager, customer)
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Badge, Card, Group, Loader, MultiSelect, Paper, ScrollArea, SegmentedControl, Select, Stack,
  Switch, Text, Title,
} from '@mantine/core';
import { Gantt, ViewMode, type Task as GanttTaskUI } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { IconCalendarTime, IconTimeline } from '@tabler/icons-react';
import { EmptyState } from '@/shared/components/EmptyState';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchList } from '@/shared/api/jsonapi/client';
import { healthHex, HealthBadge, PriorityBadge } from '@/features/projects/HealthBadge';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

interface Project extends Denormalized {
  type: 'project-entities';
  name?: string;
  code?: string;
  status?: string;
  priority?: string;
  healthStatus?: string;
  startDate?: string | null;
  endDate?: string | null;
  customer?: { id: string; type?: string; businessName?: string; name?: string };
  /** Da BE v1.8.0: PM via relationship denormalizzata. */
  projectManager?: { id: string; type?: string; firstName?: string; lastName?: string } | null;
}

interface ProjectTaskRow extends Denormalized {
  type: 'project-tasks';
  name?: string;
  startDate?: string | null;
  endDate?: string | null;
  advancement?: number | null;
  status?: string | null;
  /** Da BE v1.8.0: parent solo come relationship denormalizzata. */
  parent?: { id: string } | null;
  taskType?: string;
}

interface Tenant extends Denormalized { type: 'tenants'; name?: string; businessName?: string }
interface User extends Denormalized { type: 'users'; firstName?: string; lastName?: string; email?: string }

const VIEW_MODES: { value: ViewMode; label: string }[] = [
  { value: ViewMode.Week, label: 'Settimana' },
  { value: ViewMode.Month, label: 'Mese' },
  { value: ViewMode.QuarterDay, label: 'Trimestre' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Aperta' },
  { value: 'in-progress', label: 'In corso' },
  { value: 'on-hold', label: 'In pausa' },
  { value: 'closed', label: 'Chiusa' },
  { value: 'archived', label: 'Archiviata' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export const Route = createFileRoute('/_app/projects/gantt')({ component: MultiProjectGanttPage });

function MultiProjectGanttPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>(ViewMode.Month);
  const [showArchive, setShowArchive] = useState(false);
  const [statusF, setStatusF] = useState<string[]>([]);
  const [priorityF, setPriorityF] = useState<string[]>([]);
  const [managerF, setManagerF] = useState<string | null>(null);
  const [customerF, setCustomerF] = useState<string | null>(null);

  const projectsQ = useQuery({
    queryKey: ['projects-multi-gantt'],
    queryFn: () => fetchList<Project>('project-entities', { page: { size: 200 }, include: ['customer', 'projectManager'] }),
  });
  const tenantsQ = useQuery({
    queryKey: ['tenants-for-multi-gantt'],
    queryFn: () => fetchList<Tenant>('tenants', { page: { size: 100 } }),
  });
  const usersQ = useQuery({
    queryKey: ['users-for-multi-gantt'],
    queryFn: () => fetchList<User>('users', { page: { size: 100 } }),
  });

  const allProjects = projectsQ.data?.data ?? [];
  const tenantOpts = (tenantsQ.data?.data ?? []).map((t) => ({ value: t.id, label: t.businessName ?? t.name ?? t.id }));
  const userOpts = (usersQ.data?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
  }));

  const filteredProjects = useMemo(() => {
    return allProjects.filter((p) => {
      if (!showArchive && p.status === 'archived') return false;
      if (statusF.length && !statusF.includes(p.status ?? '')) return false;
      if (priorityF.length && !priorityF.includes(p.priority ?? '')) return false;
      if (managerF && p.projectManager?.id !== managerF) return false;
      if (customerF) {
        const cid = p.customer && 'id' in p.customer ? p.customer.id : undefined;
        if (cid !== customerF) return false;
      }
      return true;
    });
  }, [allProjects, showArchive, statusF, priorityF, managerF, customerF]);

  const taskQueries = useQueries({
    queries: filteredProjects.map((p) => ({
      queryKey: ['project-tasks-multi-gantt', p.id],
      queryFn: () =>
        fetchList<ProjectTaskRow>('project-tasks', {
          filter: { 'project.id': { eq: p.id } },
          sort: ['sortOrder', 'startDate'],
          page: { size: 100 },
        }),
      staleTime: 30_000,
    })),
  });

  const ganttTasks = useMemo<GanttTaskUI[]>(() => {
    const today = dayjs();
    const out: GanttTaskUI[] = [];
    filteredProjects.forEach((p, idx) => {
      const tq = taskQueries[idx];
      const tasks = tq?.data?.data ?? [];
      const taskStarts = tasks.map((t) => (t.startDate ? dayjs(t.startDate) : null)).filter(Boolean) as dayjs.Dayjs[];
      const taskEnds = tasks.map((t) => (t.endDate ? dayjs(t.endDate) : null)).filter(Boolean) as dayjs.Dayjs[];
      const projStart = p.startDate
        ? dayjs(p.startDate)
        : taskStarts.length ? taskStarts.reduce((a, b) => (a.isBefore(b) ? a : b)) : today;
      const projEnd = p.endDate
        ? dayjs(p.endDate)
        : taskEnds.length ? taskEnds.reduce((a, b) => (a.isAfter(b) ? a : b)) : today.add(14, 'day');
      const avgProgress = tasks.length
        ? Math.round(tasks.reduce((s, t) => s + (t.advancement ?? 0), 0) / tasks.length)
        : 0;
      const macroColor = healthHex(p.healthStatus);
      out.push({
        id: `proj-${p.id}`,
        name: `[${p.code ?? ''}] ${p.name ?? p.id}`,
        start: projStart.toDate(),
        end: projEnd.isAfter(projStart) ? projEnd.toDate() : projStart.add(1, 'day').toDate(),
        progress: avgProgress,
        type: 'project',
        hideChildren: false,
        styles: {
          backgroundColor: macroColor,
          backgroundSelectedColor: macroColor,
          progressColor: shade(macroColor, -0.2),
          progressSelectedColor: shade(macroColor, -0.2),
        },
      });
      for (const t of tasks) {
        const start = t.startDate ? dayjs(t.startDate) : projStart;
        const end = t.endDate ? dayjs(t.endDate) : start.add(2, 'day');
        out.push({
          id: t.id,
          name: t.name ?? '(senza nome)',
          start: start.toDate(),
          end: end.isAfter(start) ? end.toDate() : start.add(1, 'day').toDate(),
          progress: t.advancement ?? 0,
          type: 'task',
          project: `proj-${p.id}`,
          styles: {
            backgroundColor: shade(macroColor, 0.25),
            backgroundSelectedColor: macroColor,
            progressColor: macroColor,
            progressSelectedColor: macroColor,
          },
        });
      }
    });
    return out;
  }, [filteredProjects, taskQueries]);

  const onDoubleClick = (task: GanttTaskUI) => {
    const id = task.id.startsWith('proj-') ? task.id.slice(5) : null;
    if (id) void navigate({ to: `/projects/${id}` });
  };

  const isLoading = projectsQ.isLoading || taskQueries.some((q) => q.isLoading);

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <Title order={1}>
          <IconTimeline size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Gantt overview
        </Title>
        <Group>
          <Switch label="Mostra archivio" checked={showArchive} onChange={(e) => setShowArchive(e.currentTarget.checked)} />
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as ViewMode)}
            data={VIEW_MODES.map((m) => ({ value: m.value, label: m.label }))}
          />
        </Group>
      </Group>

      <Group align="flex-start" wrap="nowrap">
        <Paper withBorder p="md" radius="md" style={{ minWidth: 280, maxWidth: 320 }}>
          <Stack>
            <Title order={5}>Filtri</Title>
            <MultiSelect label="Stato" data={STATUS_OPTIONS} value={statusF} onChange={setStatusF} clearable />
            <MultiSelect label="Priorità" data={PRIORITY_OPTIONS} value={priorityF} onChange={setPriorityF} clearable />
            <Select label="Project Manager" data={userOpts} value={managerF} onChange={setManagerF} clearable searchable />
            <Select label="Cliente" data={tenantOpts} value={customerF} onChange={setCustomerF} clearable searchable />
            <Stack gap={4} mt="md">
              <Title order={6}>Progetti ({filteredProjects.length})</Title>
              <ScrollArea h={300} type="auto">
                <Stack gap={4}>
                  {filteredProjects.map((p) => (
                    <Card
                      key={p.id}
                      padding="xs"
                      radius="sm"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => void navigate({ to: `/projects/${p.id}` })}
                    >
                      <Group justify="space-between" gap={4}>
                        <Text size="xs" fw={600} lineClamp={1}>{p.code ? `[${p.code}] ` : ''}{p.name}</Text>
                        <HealthBadge value={p.healthStatus} size="xs" withLabel={false} />
                      </Group>
                      <Group gap={4} mt={2}>
                        <Badge size="xs" variant="light">{p.status ?? '—'}</Badge>
                        {p.priority && <PriorityBadge value={p.priority} size="xs" />}
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          </Stack>
        </Paper>

        <Card withBorder padding="sm" radius="md" style={{ flex: 1, minWidth: 0 }}>
          {isLoading && <Loader />}
          {!isLoading && ganttTasks.length === 0 && (
            <EmptyState
              icon={IconCalendarTime}
              title="Nessun progetto da visualizzare"
              description="Modifica i filtri o aggiungi una nuova commessa per popolare il Gantt."
              actionLabel="Vai alle commesse"
              onAction={() => void navigate({ to: '/projects' })}
            />
          )}
          {!isLoading && ganttTasks.length > 0 && (
            <div style={{ minHeight: 500, overflow: 'auto' }}>
              <Gantt
                tasks={ganttTasks}
                viewMode={view}
                locale="it"
                columnWidth={view === ViewMode.Week ? 60 : view === ViewMode.Month ? 80 : 40}
                listCellWidth="240px"
                barCornerRadius={4}
                fontFamily="var(--mantine-font-family)"
                fontSize="12px"
                onDoubleClick={onDoubleClick}
              />
            </div>
          )}
        </Card>
      </Group>
    </Stack>
  );
}

/** Shade hex color (positive = lighter, negative = darker). */
function shade(hex: string, amt: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, ((n >> 16) & 0xff) + Math.round(255 * amt)));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) + Math.round(255 * amt)));
  const b = Math.max(0, Math.min(255, (n & 0xff) + Math.round(255 * amt)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
